import {
    appUrl,
    getRedirectsFromCommonToRegionalUrls,
} from '@frontend/AppUrl'
import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {ErrorBoundary} from '@frontend/components/ErrorBoundary'
import {CabinetRoutes} from '@frontend/routes/CabinetRoutes'
import {RedirectRoute} from '@management/routes/RedirectRoute'
import {
    type ComponentClass,
    Suspense,
    useRef,
} from 'react'
import {
    type Location,
    Outlet,
    Route,
    Routes,
    useLocation,
} from 'react-router-dom'
import {
    CSSTransition,
    SwitchTransition,
} from 'react-transition-group'
import {
    NavigationService,
    useNavigationService,
} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {AppConfig} from '../AppConfig'
import {AppPages} from './AppPages'
import {PrivateRoute} from './PrivateRoute'
import {PublicRoute} from './PublicRoute'
import {QuickAdvanceRoutes} from './QuickAdvanceRoutes'

// Обертка на случай, если на странице произойдет ошибка при рендеринге.
const ErrorBoundaryComponent: ComponentClass = ErrorBoundary as unknown as ComponentClass

// Список путей приложения.
export function AppRoutes() {

    const location = useLocation()

    const transitionRef = useRef<HTMLDivElement>(null)

    // Инициируем использование NavigationService.
    useNavigationService()

    return (
        <SwitchTransition mode="out-in">
            <CSSTransition
                key={getTransitionKey(location, NavigationService.getNestedLocationKey())}
                classNames="page-switch"
                timeout={300}
                nodeRef={transitionRef}
            >
                <div
                    ref={transitionRef}
                    className="flex-1 d-flex flex-column align-items-stretch justify-content-sm-start"
                >
                    <Suspense
                        fallback={
                            <AsyncDataLoading
                                loading={true}
                                render={() => null}
                            />
                        }
                    >
                        <Routes location={location}>

                            {/* Информационные публичные страницы */}

                            <Route
                                path={appUrl().about}
                                element={
                                    <PublicRoute id="page-about">
                                        <AppPages.AboutPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Объявления и исполнители услуг */}

                            <Route
                                element={
                                    <PublicRoute
                                        id="offered-items"
                                        notReloadable
                                    >
                                        <Outlet />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            >
                                {/* Стартовая страница */}
                                <Route
                                    path={appUrl().index}
                                    Component={AppPages.HomePage}
                                    ErrorBoundary={ErrorBoundaryComponent}
                                />

                                {/* Все объявления (без категории) */}
                                <Route
                                    path={appUrl().ads.all}
                                    Component={AppPages.AdsPage}
                                    ErrorBoundary={ErrorBoundaryComponent}
                                />

                                {/* Объявления в категории */}
                                <Route
                                    path={appUrl().ads.category}
                                    Component={AppPages.AdsPage}
                                    ErrorBoundary={ErrorBoundaryComponent}
                                />

                                {/* Услуги в категории */}
                                <Route
                                    path={appUrl().services.category}
                                    Component={AppPages.ServicesPage}
                                    ErrorBoundary={ErrorBoundaryComponent}
                                />
                            </Route>

                            {/* Детали объявления */}
                            <Route
                                path={appUrl().ads.details}
                                element={
                                    <PublicRoute id="offered-item-details">
                                        <AppPages.AdDetailsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Детали услуги */}
                            <Route
                                path={appUrl().services.details}
                                element={
                                    <PublicRoute
                                        notReloadable
                                        id="executor-details"
                                    >
                                        <AppPages.ExecutorDetailsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Продавец */}

                            <Route
                                path={appUrl().seller.details}
                                element={
                                    <PublicRoute>
                                        <AppPages.SellerDetailsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().seller.reviews}
                                element={
                                    <PublicRoute>
                                        <AppPages.SellerReviewsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Заказчик услуг */}

                            <Route
                                path={appUrl().serviceRequester.details}
                                element={
                                    <PublicRoute>
                                        <AppPages.ServiceRequesterDetailsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Владелец исполнителя услуг */}

                            <Route
                                path={appUrl().services.ownerDetails}
                                element={
                                    <PublicRoute>
                                        <AppPages.ServiceOwnerDetailsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Новости */}

                            {AppConfig.features.news !== false && (
                                <>
                                    <Route
                                        path={appUrl().news.list}
                                        element={
                                            <PublicRoute id="news">
                                                <AppPages.NewsPage />
                                            </PublicRoute>
                                        }
                                        ErrorBoundary={ErrorBoundaryComponent}
                                    />

                                    <Route
                                        path={appUrl().news.details}
                                        element={
                                            <PublicRoute id="news-item-details">
                                                <AppPages.NewsItemDetailsPage />
                                            </PublicRoute>
                                        }
                                        ErrorBoundary={ErrorBoundaryComponent}
                                    />
                                </>
                            )}

                            {/* Помощь */}

                            <Route
                                path={appUrl().support.index}
                                element={
                                    <PublicRoute id="page-support">
                                        <AppPages.SupportPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().support.category}
                                element={
                                    <PublicRoute id="page-support-category">
                                        <AppPages.SupportCategoryPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().support.article}
                                element={
                                    <PublicRoute id="page-support-article">
                                        <AppPages.SupportArticlePage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().support.askQuestion}
                                element={
                                    <PrivateRoute id="page-support-ask-question">
                                        <AppPages.ContactSupportFormPage />
                                    </PrivateRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Правила */}

                            <Route
                                path={appUrl().support.rulesAndDocuments}
                                element={
                                    <PublicRoute id="page-rules-and-documents">
                                        <AppPages.RulesAndDocumentsPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().support.rulesAndDocumentsArticle}
                                element={
                                    <PublicRoute id="page-rules-and-documents-article">
                                        <AppPages.RulesAndDocumentsArticlePage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Авторизация и регистрация */}

                            <Route
                                path={appUrl().login}
                                element={
                                    <PublicRoute
                                        authPage
                                        notReloadable
                                    >
                                        <AppPages.LoginPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().registration}
                                element={
                                    <PublicRoute
                                        authPage
                                        notReloadable
                                    >
                                        <AppPages.RegistrationPage />
                                    </PublicRoute>
                                }
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            <Route
                                path={appUrl().logout}
                                Component={AppPages.LogoutPage}
                                ErrorBoundary={ErrorBoundaryComponent}
                            />

                            {/* Быстрый аванс */}

                            {QuickAdvanceRoutes()}

                            {/* Личный кабинет */}

                            {CabinetRoutes()}

                            {/* UI Demo */}
                            {AppPages.UiDemoPage && (
                                <Route
                                    path={appUrl().uiDemo}
                                    element={
                                        <PublicRoute id="ui-demo">
                                            <AppPages.UiDemoPage />
                                        </PublicRoute>
                                    }
                                    ErrorBoundary={ErrorBoundaryComponent}
                                />
                            )}

                            {/* Перенаправления с глобальных URL на региональные (/vn/about => /vn/ve-dotoku) */}

                            {
                                Object.entries(getRedirectsFromCommonToRegionalUrls())
                                    .map(urls => (
                                        <Route
                                            key={urls[0]}
                                            path={urls[0]}
                                            element={
                                                <RedirectRoute
                                                    to={urls[1]}
                                                    passUrlParams
                                                    passUrlQueryParams
                                                />
                                            }
                                        />
                                    ))
                            }

                            {/* Страница не найдена */}

                            <Route
                                path="*"
                                element={
                                    <AppPages.ErrorPage
                                        code={404}
                                        preferHistoryBack={true}
                                    />
                                }
                            />
                        </Routes>
                    </Suspense>
                </div>
            </CSSTransition>
        </SwitchTransition>
    )
}

/*
 * Генерирует ключ для анимации смены страницы.
 * Анимация не должна отрабатывать для одного итого же URL более одного раза подряд.
 */
function getTransitionKey(location: Location, nestedLocationKey: null | string): string {
    // location.pathname должен начинаться с nestedLocationKey.
    if (nestedLocationKey && location.pathname.search(nestedLocationKey) === 0) {
        return nestedLocationKey
    }
    // Отменяем анимацию для страниц ЛК.
    if (location.pathname.startsWith(appUrl().cabinet.root)) {
        return appUrl().cabinet.root
    }
    return location.pathname
}
